<template>
    <div
            class="type-ahead"
            :class="{'is-focused': isFocused && hasFocusableState}"
    >
        <b-input-group>
            <slot
                    name="before"
            />

            <b-form-input
                    :id="inputId"
                    :disabled="disabled"
                    :class="inputClass"
                    ref="typeAheadInput"
                    class="type-ahead__input"
                    :placeholder="placeholder"
                    :value="searchValue"
                    @update="handleSearch"
                    @focus="handleFocus"
                    @focusout="handleFocusOut"
            />

            <slot
                    v-if="!isFocused"
                    name="after"
            />

            <b-input-group-append
                    class="type-ahead__search-clear--wrapper"
                    v-if="isFocused"
            >
                <button
                        class="input-group-text-coherent type-ahead__search-clear--button"
                        @click="clear"
                >
                    <i class="fas fa-times-circle" />
                </button>
            </b-input-group-append>
        </b-input-group>

        <div
                class="results-wrapper"
                v-if="dropdownState"
        >
            <ul
                    class="dropdown-menu"
                    :class="{'p-0': noPadding}"
            >
                <element-loader
                        v-if="isLoading"
                        :loading="true"
                        :height="30"
                />

                <li
                        v-if="searchValue && !isLoading && !collection.length && !this.allowUserValue && this.allowNoResults"
                        :disabled="false"
                        class="dropdown-item"
                >
                    Search by <em>{{ searchValue }}</em> has no results!
                </li>

                <b-list-group class="scrollable-group">
                    <b-dropdown-item
                            v-for="(resource) of Object.values(this.collection.appResources || {})"
                            :key="resource.uuid"
                            @click="handleSetResource(resource)"
                    >
                        <text-highlight
                                :queries="searchValueArray"
                                highlight-component="b"
                        >
                            {{ resourceTitle(resource) }}
                        </text-highlight>
                    </b-dropdown-item>

                    <b-dropdown-item
                            v-if="searchValue && !isLoading && this.allowUserValue"
                            @click="handleSetResource(searchValue)"
                    >
                        {{ searchValue }}
                    </b-dropdown-item>
                </b-list-group>
                <slot name="extension" />
            </ul>
        </div>
    </div>
</template>
<script>
import './AddressTypeAhead.scss';
import AddressAppResource from '@milestoneslabs/milestones-core-js/src/AppResources/AddressAppResource';
import DynamicTypeAhead, {UPDATE_SEARCH_INPUT_RAW_VALUE} from "../../components/DynamicTypeAhead/DynamicTypeAhead.vue";
import TextHighlight from 'vue-text-highlight';
import debounce from "lodash/debounce";
import {isEmpty, find} from 'lodash';
import ShowNotificationMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/ShowNotificationMixin";
import {addressCoreLogicPropertyMatch, addressesToCollection, standardizeAddress} from "../../helpers/coreLogic";
import AppResourceCollection from "@milestoneslabs/milestones-js-engine/src/Data/AppResourceCollection";
const LANDING_PAGE_HOME = 'landing-page.home';

export default {
    name: 'AddressTypeAhead',
    extends: DynamicTypeAhead,
    components: {
        TextHighlight
    },
    mixins: [
        ShowNotificationMixin
    ],
    props: {
        resourceType: {
            type: String,
            default: 'address',
            required: false,
        },
        labelField: {
            type: [String, Function],
            default: 'fullAddress',
        },
        isPlain: {
            type: [Boolean],
            default: false,
        },
        hasFocusableState: {
            type: [Boolean],
            default: true
        },
        reducer: {
            type: [Function, String],
            default(resource) {
                if (this.isPlain) {
                    if (this.allowUserValue && !(resource instanceof AddressAppResource)) {
                        return resource;
                    }
                    return resource.fullAddress;
                }
                return resource;
            },
        },
        allowNoResults: {
            type: Boolean,
            default: true,
        },
        resetSearchedValue: {
            type: Boolean,
            default: false,
        },

        noPadding: {
            type: Boolean,
            default: false,
        },
        isZipCodeRequired: {
            type: Boolean,
            default: false,
        },
        preselectedAddressName: {
            type: String,
            default: '',
            required: false,
        },
        preselectedAddress: {
            type: Object,
            default: () => {
            }
        },
        shouldSearchPreselectedAddress: {
            type: Boolean,
            default: false,
        },
        token: {
            type: String,
            default: null,
            required: false,
        },
        invitationKey: {
            type: String,
            default: null,
            required: false,
        },
        isOptInPendingInvitation: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isFocused: false,
            searchValueArray: [],
            userGeolocation: '',
        };
    },
    methods: {
        async searchResources(searchValue) {
            if (searchValue?.length < 1) {
                this.dropdownState = false;
                return;
            }

            this.loading = true;
            try {
                this.searchValueArray = searchValue.split(/[\s,]+/);
                await this.doAddressMatch(searchValue);
            } catch (e) {
                console.error(e);
                this.showNotification({
                    message: 'Something went wrong',
                    type: 'error',
                });
            } finally {
                this.loading = false;
            }
        },
        async doAddressMatch(searchValue) {
            try {
                let additionalData = {};

                if (this.isLandingPageHome) {
                    additionalData = {
                        landingPageUuid: window.landingPageUuid,
                    };
                }
                if (this.isExternalInvitation) {
                    additionalData = {
                        token: this.token,
                        inviteKey: this.invitationKey
                    };
                }
                const results = await addressCoreLogicPropertyMatch(searchValue, additionalData);
                this.collection = addressesToCollection(results);
            }
            catch(e) {
                console.log(e);
            }
        },
        handleFocus() {
            this.isFocused = true;
            if (!this.hasAfterSlot()) {
                this.$refs.typeAheadInput.$el.classList.add('border-right-0');
            }
            this.getGeolocation();
            if (
                    this.dropdownState ||
                    !this.searchValue
            ) {
                return;
            }
            if (this.collection.length) {
                this.dropdownState = true;
            }
        },
        async handleSetResource(resource) {
            if (this.isZipCodeRequired) {
                let resourceWithZipCode = await this.searchValidZipCodeAddress(resource);
                this.internalModel = this.reduce(resourceWithZipCode);
                this.rawResource = resourceWithZipCode;
                this.dropdownState = false;
                this.searchValue = this.getTitle();
                return;
            }
            this.internalModel = this.reduce(resource);
            this.rawResource = resource;
            this.dropdownState = false;
            this.searchValue = this.getTitle();
            this.$emit('set-resource', resource);
        },
        async searchValidZipCodeAddress(resource) {
            let params = {
                delivery_line: resource.deliveryLine,
                city: resource.city,
                state: resource.state,
                zip: resource.zip
            };

            let isPublic = this.isLandingPageHome || this.isExternalInvitation;

            this.loading = true;
            try {
                let addressResource = new AddressAppResource({
                    attributes: resource.attributes,
                });

                addressResource.setAttribute('street', resource.attributes.street);
                addressResource.isAddressFound = false;

                const standardizeResult = await standardizeAddress(params, isPublic);

                addressResource.setAttribute('latitude', standardizeResult?.latitude || null);
                addressResource.setAttribute('longitude', standardizeResult?.longitude || null);

                if (!isEmpty(standardizeResult)) {
                    addressResource.setAttribute('streetNumber', standardizeResult?.street_number);
                    addressResource.setAttribute('street', standardizeResult?.street);
                    addressResource.setAttribute('preDirection', standardizeResult?.pre_direction);
                    addressResource.setAttribute('postDirection', standardizeResult?.post_direction);
                    addressResource.setAttribute('suffix', standardizeResult?.suffix);
                    addressResource.setAttribute('secondaryNumber', standardizeResult?.secondary_number);
                    addressResource.setAttribute('zip', standardizeResult?.postal_code);
                    addressResource.setAttribute('postalCode', standardizeResult?.zip || standardizeResult?.postal_code);
                    addressResource.setAttribute('county', standardizeResult?.county);
                    addressResource.setAttribute('deliveryLine', standardizeResult?.delivery_line);
                    addressResource.setAttribute('fullAddress', standardizeResult?.full_address);
                    addressResource.setAttribute('state', standardizeResult?.state);
                    addressResource.setAttribute('city', standardizeResult?.city);
                    addressResource.setAttribute('country', standardizeResult?.country);
                    addressResource.setAttribute('corelogicClip', standardizeResult?.corelogic_clip);
                    addressResource.isAddressFound = true;
                }

                return addressResource;
            } catch (e) {
                console.error(e);
                this.showNotification({
                    message: 'Something went wrong',
                    type: 'error',
                });
                // Return original resource to allow flow to continue
                return resource;
            } finally {
                this.loading = false;
            }
        },
        hasAfterSlot() {
            return !!this.$slots.after;
        },
        handleSearch: debounce(async function (searchValue) {
            this.collection = new AppResourceCollection(this.resourceType);

            searchValue = searchValue.toString().trim();
            this.searchValue = searchValue;

            if (!searchValue || (typeof searchValue === 'string' && searchValue.length < 1)) {
                this.dropdownState = false;
                return;
            }

            this.searchValueArray = searchValue.split(' ');
            searchValue = searchValue.trim();
            this.dropdownState = true;
            await this.searchResources(searchValue);

            if (this.allowNoResults || this.collection.length) {
                this.dropdownState = true;
            }
            if (!this.allowNoResults && !this.collection.length) {
                this.dropdownState = false;
            }

            if (this.$bus) {
                this.$bus.$emit(UPDATE_SEARCH_INPUT_RAW_VALUE, searchValue);
            }
        }, 600),
        clear() {
            this.searchValue = '';
            this.rawResource = '';
            this.dropdownState = false;

            if (this.isZipCodeRequired) {
                this.internalModel = new AddressAppResource({});

                return;
            }

            this.internalModel = null;
        },
        handleFocusOut(event) {
            if (
                    event.relatedTarget &&
                    event.relatedTarget.classList.contains('type-ahead__search-clear--button')
            ) {
                return;
            }
            if (!this.hasAfterSlot()) {
                this.$refs.typeAheadInput.$el.classList.remove('border-right-0');
            }
            this.isFocused = false;
        },
        getGeolocation() {
            if ('geolocation' in navigator) {
                navigator.geolocation.getCurrentPosition((position) => {
                    if (position.coords) {
                        this.userGeolocation = `${position.coords.latitude}:${position.coords.longitude}`;
                    }
                });
            }
        },
        getTitle() {
            if (!isEmpty(this.preselectedAddressName)) {
                return this.preselectedAddressName;
            }
            if (this.rawResource && !Array.isArray(this.rawResource)) {
                return this.resourceTitle(this.rawResource);
            }
            if (this.allowUserValue && this.internalModel) {
                return this.internalModel;
            }
        },
        getPreselectedAddressMatched(address) {
            if (true === this.isOptInPendingInvitation) {
                return;
            }
            return find(this.collection.appResources, (elem) => {
                let [suffixComparation, streetNumberComparation, countryComparation, preDirectionComparation,
                    postDirectionComparation] = Array(5).fill(true);

                if (address.suffix) {
                    suffixComparation = address.suffix?.toLowerCase() === elem.suffix?.toLowerCase();
                }

                if (address.streetNumber) {
                    streetNumberComparation = address.streetNumber === elem.streetNumber;
                }

                if (address.country) {
                    countryComparation = address.country?.toLowerCase() === elem.country?.toLowerCase();
                }

                if (address.preDirection) {
                    preDirectionComparation = address.preDirection?.toLowerCase() === elem.preDirection?.toLowerCase();
                }

                if (address.postDirection) {
                    postDirectionComparation = address.postDirection?.toLowerCase() === elem.postDirection?.toLowerCase();
                }

                return elem.city?.toLowerCase() === address.city?.toLowerCase() &&
                        elem.state?.toLowerCase() === address.state?.toLowerCase() &&
                        elem.deliveryLine?.toLowerCase() === address.deliveryLine?.toLowerCase() &&
                        elem.zip == address.zip &&
                        countryComparation &&
                        suffixComparation &&
                        streetNumberComparation &&
                        preDirectionComparation &&
                        postDirectionComparation;
            });
        }
    },
    computed: {
        placeholder() {
            if (this.resetSearchedValue) {
                return this.placeholderFallback;
            }

            if (this.rawResource && this.rawResource instanceof AddressAppResource) {
                const resourceTitle = this.resourceTitle(this.rawResource)
                return resourceTitle ? resourceTitle : this.placeholderFallback
            }

            if (null === this.internalModel) {
                return this.placeholderFallback;
            }

            if ((this.allowUserValue && this.internalModel) || (this.isPlain && this.internalModel)) {
                return this.internalModel;
            }
            return this.placeholderFallback;
        },
        isExternalInvitation() {
            return this.invitationKey != null && this.token != null;
        },
        isLandingPageHome() {
            return LANDING_PAGE_HOME === this.$route?.name && window.landingPageUuid?.length > 0;
        }
    },
    watch: {
        value(newValue) {
            this.rawResource = newValue instanceof AddressAppResource ? newValue : null;
        },
        resetSearchedValue(value) {
            if (!value) {
                return;
            }
            this.$emit('update:resetSearchedValue', false);
            if (isEmpty(this.searchValue)) {
                return;
            }
            this.searchValue = '';
        },
        preselectedAddressName() {
            this.searchValue = this.getTitle();
        },
        async preselectedAddress(address) {
            if (
                    Object.entries(address)?.length === 0 ||
                    !this.shouldSearchPreselectedAddress ||
                    this.preselectedAddressName.length === 0
            ) {
                return;
            }

            await this.searchResources(this.preselectedAddressName);

            let addressMatched = this.getPreselectedAddressMatched(address);
            if (addressMatched) {
                await this.handleSetResource(addressMatched);
            } else {
                this.dropdownState = true;
            }
        },
    },
};
</script>
